import { observer } from "mobx-react";
import { useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { PieChartTooltip } from "./PieChartToolTip";
import { CustomLabel } from "./CustomLabel";
import { PieChartItem, pieChartProps } from "./shared";
import { Text } from "@chakra-ui/react";
import { t } from "i18next";
import {
  AttributeDefinition,
  AttributeNumber,
  AttributeSelect,
  AttributeTextArea,
  AttributeType,
} from "@keita/lanepatrol-sdk";
import { getAttributeValue } from "../../../../coding/utils/attribute-value";
import { AttributeUtils } from "../../../../coding/utils/attribute-utils";
import {
  mapGradient,
  mapGradientEnd,
  mapGradientStart,
  mapGradientStep,
} from "../../../../map/utils/map-utils";
import { useStores } from "../../../../shared/stores/RootStore";

interface Props {
  attributeDefinition: AttributeDefinition;
}
const AttributePieChartBase: React.FC<Props> = ({ attributeDefinition }) => {
  const { filterStore, codingStore } = useStores();

  const data = useMemo(() => {
    const totalNodes = filterStore.filteredNodes.length;
    if (!totalNodes) return;

    const buildSelectData = () => {
      const dataMap = new Map<string, number>();
      const box = attributeDefinition as AttributeSelect;
      const notCodedKey = "";

      box.options.forEach((option) => dataMap.set(option.value, 0));
      dataMap.set(notCodedKey, 0);

      filterStore.filteredNodes.forEach((node) => {
        const codingEntry =
          filterStore.originalOrForecastedAllNodeCodingsByWayId
            .get(node.osaWayId)
            ?.get(node.id!);
        if (!codingEntry) return;

        const value = getAttributeValue(box.name, codingEntry.attributes);

        incrMapValue(dataMap, value ?? notCodedKey);
      });

      const data: PieChartItem[] = [];
      for (const [optionValue, totalMatching] of dataMap) {
        const optionIndex = box.options.findIndex(
          (option) => option.value === optionValue,
        );

        if (totalMatching === 0) continue;

        data.push({
          name:
            optionValue === notCodedKey
              ? t(`coding:null`)
              : AttributeUtils.getOptionLabel(optionValue),
          value:
            ((totalMatching / totalNodes) * filterStore.filteredDistance) /
            1000,
          color:
            optionValue === notCodedKey
              ? "#000"
              : mapGradientStep(box.options.length, optionIndex),
        });
      }

      return data;
    };

    const buildTextAreaData = () => {
      const dataMap = new Map<number, number>();
      const box = attributeDefinition as AttributeTextArea;

      dataMap.set(0, 0); // not filled
      dataMap.set(1, 0); // filled

      filterStore.filteredNodes.forEach((node) => {
        const codingEntry = codingStore.allNodeCodingsByWayId
          .get(node.osaWayId)
          ?.get(node.id!);
        if (!codingEntry) return;

        const value = getAttributeValue(box.name, codingEntry.attributes);
        if (value) {
          incrMapValue(dataMap, 1);
        } else {
          incrMapValue(dataMap, 0);
        }
      });

      const data: PieChartItem[] = [];
      for (const [optionValue, totalMatching] of dataMap) {
        if (totalMatching === 0) continue;

        data.push({
          name: optionValue === 1 ? "Filled" : "Not filled",
          value:
            ((totalMatching / totalNodes) * filterStore.filteredDistance) /
            1000,
          color: optionValue === 1 ? mapGradientStart : mapGradientEnd,
        });
      }

      return data;
    };

    const buildRoadSpeedLimitData = () => {
      const dataMap = new Map<number, number>();
      const box = attributeDefinition as AttributeNumber;
      const notCodedIndex = -1;

      dataMap.set(notCodedIndex, 0);
      const nSteps = 9;
      [...Array(nSteps).keys()].forEach((step) => dataMap.set(step, 0));

      filterStore.filteredNodes.forEach((node) => {
        const codingEntry = codingStore.allNodeCodingsByWayId
          .get(node.osaWayId)
          ?.get(node.id!);
        if (!codingEntry) return;

        const value = getAttributeValue(box.name, codingEntry.attributes);
        if (value === null) {
          incrMapValue(dataMap, notCodedIndex);
          return;
        }

        const normalizedValue = Math.floor((value as number) / 10);
        const limitedValue = Math.max(Math.min(normalizedValue, nSteps - 1), 0);

        incrMapValue(dataMap, limitedValue);
      });

      const data: PieChartItem[] = [];
      for (const [optionValue, totalMatching] of dataMap) {
        if (totalMatching === 0) continue;

        data.push({
          name:
            optionValue === notCodedIndex
              ? t(`coding:null`)
              : optionValue === nSteps - 1
              ? `> 80 km/h`
              : `${optionValue * 10} to ${(optionValue + 1) * 10} km/h`,
          value:
            ((totalMatching / totalNodes) * filterStore.filteredDistance) /
            1000,
          color:
            optionValue === notCodedIndex
              ? "#000"
              : `#${mapGradient.hsvAt(optionValue / (nSteps - 1)).toHex()}`,
        });
      }

      return data;
    };

    switch (attributeDefinition.type) {
      case AttributeType.Select:
        return buildSelectData();
      case AttributeType.TextArea:
        return buildTextAreaData();
      case AttributeType.Number:
        return buildRoadSpeedLimitData();
    }

    // eslint-disable-next-line
  }, [filterStore.filteredNodes, attributeDefinition]);

  if (!data)
    return (
      <Text py={2} px={4} fontSize="xs">
        {t(`map:pieChart.noData`)}
      </Text>
    );

  return (
    <PieChart {...pieChartProps}>
      <Tooltip content={<PieChartTooltip />} />

      <Pie
        nameKey="name"
        dataKey="value"
        label={CustomLabel}
        data={data}
        outerRadius={80}
      >
        {data.map((item, i) => (
          <Cell key={i} fill={item.color} />
        ))}
      </Pie>
    </PieChart>
  );
};

export const AttributePieChart = observer(AttributePieChartBase);

const incrMapValue = (map: Map<any, number>, key: any) =>
  map.set(key, map.get(key)! + 1);
